/**
 * Splits the shows a contractor is on into "already happened" and "coming up",
 * for the Recent Shows block of the contact card (client request 2026-08-21:
 * *"Can we have a link to recent shows worked? Maybe 5"*).
 *
 * The data comes from `GET /big-search/people/:personId/events?involvement=worked`,
 * which returns every show the person was scheduled on, sorted by start date
 * DESC. That single ordering answers neither question on its own: the top of
 * that list is whatever is booked FURTHEST INTO THE FUTURE, so "the 5 most
 * recent" read off the front of it would be five shows that have not happened.
 * Hence the split here rather than a plain `.slice(0, 5)`.
 *
 * ⚠️ Date parsing — the same trap `payDate.ts` documents. An event carries
 * dates under two generations of field:
 *   - `eventStartDate` / `eventEndDate` — real `Date` in Mongo, arriving over
 *     JSON as `"2026-07-16T00:00:00.000Z"`;
 *   - `eventStart` / `eventEnd` — plain day STRINGS (`"2026-07-16"`), the legacy
 *     pair, still the only ones filled on older documents.
 * `new Date("2026-07-16")` is parsed as UTC midnight, which reads back as
 * July 15th in every US timezone. A show that ended today would sort as ended
 * yesterday — harmless in a list, but it is the same one-day shift that already
 * bit the SOW pay date. So every comparison below stays in UTC, and "today" is
 * the viewer's LOCAL calendar day pinned to UTC midnight: at 11pm in Chicago
 * "today" must still be the 25th, not the 26th.
 */

import type { BigSearchPersonEvent } from "../../api/big-search";

/** How many shows each bucket shows. The client asked for "maybe 5". */
export const MAX_SHOWS_PER_BUCKET = 5;

/** `"2026-07-16"` or `"2026-07-16T00:00:00.000Z"` → captures the day part. */
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;
/** Legacy documents may still carry the display format `"07/16/2026"`. */
const US_DAY = /^(\d{2})\/(\d{2})\/(\d{4})/;

function toUtcDay(isoDay: string): number | null {
  const ms = new Date(`${isoDay}T00:00:00Z`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * An event day (either field generation) as a UTC-midnight timestamp, or `null`
 * when there is nothing usable. `Date` instances are accepted because the same
 * helper runs against store data that has not been through JSON.
 */
export function parseEventDay(value?: string | Date | null): number | null {
  if (value instanceof Date) {
    const ms = value.getTime();
    if (Number.isNaN(ms)) return null;
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, "0");
    const d = String(value.getUTCDate()).padStart(2, "0");
    return toUtcDay(`${y}-${m}-${d}`);
  }

  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const iso = ISO_DAY.exec(raw);
  if (iso) {
    const [, year, month, day] = iso;
    return toUtcDay(`${year}-${month}-${day}`);
  }

  const us = US_DAY.exec(raw);
  if (us) {
    const [, month, day, year] = us;
    return toUtcDay(`${year}-${month}-${day}`);
  }

  return null;
}

/** First day of the show — the new field wins, the legacy string is the fallback. */
export function showStartDay(ev: BigSearchPersonEvent): number | null {
  return parseEventDay(ev?.eventStartDate) ?? parseEventDay(ev?.eventStart);
}

/**
 * Last day of the show. Falls back to the start day because single-day events
 * created before the end date became required have it empty — without the
 * fallback those would count as undated and disappear from both buckets.
 */
export function showEndDay(ev: BigSearchPersonEvent): number | null {
  return (
    parseEventDay(ev?.eventEndDate) ??
    parseEventDay(ev?.eventEnd) ??
    showStartDay(ev)
  );
}

/** The viewer's local calendar day, pinned to UTC midnight so it compares with event days. */
export function startOfTodayUtc(now: Date = new Date()): number {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return toUtcDay(`${y}-${m}-${d}`) as number;
}

export interface ContractorShowBuckets {
  /** Already finished, most recent first. Capped at `MAX_SHOWS_PER_BUCKET`. */
  past: BigSearchPersonEvent[];
  /** Running today or booked ahead, soonest first. Capped the same way. */
  upcoming: BigSearchPersonEvent[];
  /** Totals BEFORE the cap, so the card can show "+N more" honestly. */
  pastTotal: number;
  upcomingTotal: number;
  /**
   * Shows with no usable date in any of the four fields. Deliberately in
   * neither bucket — nothing here can honestly call them recent or upcoming —
   * but counted, so they are never silently swallowed.
   */
  undatedTotal: number;
}

/**
 * A show counts as past only once its LAST day is behind us: one that started
 * yesterday and runs through tomorrow is in progress, and the person is on site
 * right now. Grouping that under "recent" would be wrong in the one case an
 * admin most needs to be right about, so in-progress sits at the top of
 * upcoming instead.
 */
export function splitShowsByTime(
  events: BigSearchPersonEvent[] | null | undefined,
  now: Date = new Date(),
): ContractorShowBuckets {
  const today = startOfTodayUtc(now);
  const past: Array<{ ev: BigSearchPersonEvent; key: number }> = [];
  const upcoming: Array<{ ev: BigSearchPersonEvent; key: number }> = [];
  let undatedTotal = 0;

  for (const ev of Array.isArray(events) ? events : []) {
    if (!ev) continue;
    const end = showEndDay(ev);
    if (end === null) {
      undatedTotal += 1;
      continue;
    }
    // Sorting is always by the START day: that is the date the card prints, and
    // ordering by one date while displaying another looks like a broken sort.
    const key = showStartDay(ev) ?? end;
    if (end < today) past.push({ ev, key });
    else upcoming.push({ ev, key });
  }

  past.sort((a, b) => b.key - a.key); // most recent first
  upcoming.sort((a, b) => a.key - b.key); // soonest first

  return {
    past: past.slice(0, MAX_SHOWS_PER_BUCKET).map((x) => x.ev),
    upcoming: upcoming.slice(0, MAX_SHOWS_PER_BUCKET).map((x) => x.ev),
    pastTotal: past.length,
    upcomingTotal: upcoming.length,
    undatedTotal,
  };
}
